import { useNavigate } from "react-router-dom";
import { authClient } from "@/lib/auth-client";
import { toast } from "sonner";
import { useTranslations } from "@/hooks/use-translations";

export function useAuth() {
	const navigate = useNavigate();
	const t = useTranslations("auth");
	const { data: session, isPending, error } = authClient.useSession();

	const handleSignIn = async (email: string, password: string) => {
		const result = await authClient.signIn.email({
			email,
			password,
		});

		if (result.error) {
			// Check for "User not found" error and provide a friendlier message
			const errorMessage = result.error.message?.toLowerCase() || "";
			if (
				errorMessage.includes("user not found") ||
				result.error.code === "USER_NOT_FOUND"
			) {
				throw new Error("USER_NOT_FOUND");
			}
			throw new Error(result.error.message);
		}

		// "/" sends the user to the first page they can open.
		navigate("/");
		return result;
	};

	const handleSignUp = async (
		email: string,
		password: string,
		name: string,
	) => {
		const result = await authClient.signUp.email({
			email,
			password,
			name,
		});

		if (result.error) {
			throw new Error(result.error.message);
		}

		// Show first toast with shorter duration
		toast.success(t("signupSuccess"), { duration: 4000 });

		// Show second toast after first one dismisses (not stacked)
		setTimeout(() => {
			toast.info(t("checkEmailVerification"), { duration: 4000 });
		}, 4200); // Slightly longer than first toast duration

		navigate("/login");
		return result;
	};

	const handleSignOut = async () => {
		await authClient.signOut();
		navigate("/login");
	};

	return {
		user: session?.user ?? null,
		session: session?.session ?? null,
		isLoading: isPending,
		isAuthenticated: !!session?.user,
		error,
		signIn: handleSignIn,
		signUp: handleSignUp,
		signOut: handleSignOut,
	};
}


